import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { MessageSquareText, Loader2, Check, X } from 'lucide-react';

export interface DriverNote { note: string; updated_at?: string | null; by?: string }
const MAX = 300;

// Iter 802: sjåførens korte merknad per tur (f.eks. forsinkelse) fra kjøreplan-lenken — vises for kontoret i Ukesplanen.
export function TripNoteEditor({ tripNumber, note, onSave, lang }: { tripNumber: string; note?: DriverNote | null; onSave: (tripNumber: string, note: string) => Promise<boolean>; lang: string }) {
  const { t } = useTranslation();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(note?.note || '');
  const [saving, setSaving] = useState(false);
  const when = note?.updated_at ? new Date(note.updated_at).toLocaleString(lang === 'en' ? 'en-GB' : 'nb-NO', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }) : '';
  const save = async () => {
    setSaving(true);
    const ok = await onSave(tripNumber, draft.trim());
    setSaving(false);
    if (ok) setEditing(false);
  };
  if (!editing) {
    return (
      <div className="w-full flex flex-wrap items-center gap-2 text-xs" data-testid={`sp-note-${tripNumber}`} data-state={note?.note ? 'set' : 'empty'}>
        {note?.note ? (
          <span className="inline-flex items-start gap-1 text-amber-800 bg-amber-50 border border-amber-200 rounded px-2 py-1 max-w-full">
            <MessageSquareText className="w-3.5 h-3.5 mt-0.5 shrink-0" /><span className="break-words" data-testid={`sp-note-text-${tripNumber}`}>{note.note}</span>
            {when && <span className="text-amber-600/80 whitespace-nowrap">· {when}</span>}
          </span>
        ) : null}
        <button type="button" onClick={() => { setDraft(note?.note || ''); setEditing(true); }} className="inline-flex items-center gap-1 text-slate-600 hover:text-slate-900 underline" data-testid={`sp-note-edit-${tripNumber}`}>
          {note?.note ? t('publicDriverPlan.endreMerknad', 'Endre merknad') : <><MessageSquareText className="w-3.5 h-3.5" />{t('publicDriverPlan.leggTilMerknad', 'Legg til merknad til kontoret')}</>}
        </button>
      </div>
    );
  }
  return (
    <div className="w-full space-y-1.5" data-testid={`sp-note-form-${tripNumber}`}>
      <textarea value={draft} onChange={(e) => setDraft(e.target.value.slice(0, MAX))} rows={2} maxLength={MAX} autoFocus
        placeholder={t('publicDriverPlan.merknadPlaceholder', 'F.eks. «Forsinket 15 min pga. ferje» – kontoret ser dette i Ukesplanen')}
        className="w-full rounded-md border border-slate-300 px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-violet-300" data-testid={`sp-note-input-${tripNumber}`} />
      <div className="flex flex-wrap items-center justify-between gap-2 text-xs">
        <span className="text-slate-400">{draft.length}/{MAX}</span>
        <div className="flex gap-2">
          <button type="button" onClick={() => setEditing(false)} disabled={saving} className="inline-flex items-center gap-1 rounded-md border px-2.5 py-1 text-slate-600 hover:bg-slate-50" data-testid={`sp-note-cancel-${tripNumber}`}><X className="w-3.5 h-3.5" />{t('publicDriverPlan.avbryt', 'Avbryt')}</button>
          <button type="button" onClick={save} disabled={saving || (!draft.trim() && !note?.note)} className="inline-flex items-center gap-1 rounded-md bg-violet-600 px-2.5 py-1 font-medium text-white hover:bg-violet-700 disabled:opacity-50" data-testid={`sp-note-save-${tripNumber}`}>
            {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />}{!draft.trim() && note?.note ? t('publicDriverPlan.fjernMerknad', 'Fjern merknad') : t('publicDriverPlan.sendMerknad', 'Send til kontoret')}
          </button>
        </div>
      </div>
    </div>
  );
}
